// URL-backed state and data loading for the clothing browser.
// Every filter lives in the query string so pages are shareable and the
// back button restores the exact view.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BrowseFilters,
  EMPTY_FILTERS,
  Facets,
  StylesPage,
  fetchBrandMeta,
  fetchFacets,
  fetchStyles,
} from '../lib/clothingBrowseApi';
import { PAGE_SIZE, SortKey, SORT_OPTIONS } from '../data/clothing/browseConfig';

const csv = (v: string | null): string[] => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : []);
const num = (v: string | null): number | undefined => {
  const n = v === null ? NaN : parseFloat(v);
  return Number.isFinite(n) ? n : undefined;
};

export function parseFilters(sp: URLSearchParams): BrowseFilters {
  const sort = sp.get('sort') as SortKey | null;
  return {
    // `productTypes` and `type` are the legacy params used by ShopHero and the mega menu
    types: csv(sp.get('types') || sp.get('productTypes') || sp.get('type')),
    brands: csv(sp.get('brands')),
    genders: csv(sp.get('genders')),
    ages: csv(sp.get('ages')),
    colours: csv(sp.get('colours')),
    sizes: csv(sp.get('sizes')),
    min: num(sp.get('min')),
    max: num(sp.get('max')),
    eco: sp.get('eco') === '1',
    q: sp.get('q') || sp.get('search') || '',
    sort: sort && SORT_OPTIONS.some((o) => o.value === sort) ? sort : 'popular',
    page: Math.max(1, parseInt(sp.get('page') || '1', 10) || 1),
  };
}

export function serializeFilters(f: BrowseFilters): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.types.length) sp.set('types', f.types.join(','));
  if (f.brands.length) sp.set('brands', f.brands.join(','));
  if (f.genders.length) sp.set('genders', f.genders.join(','));
  if (f.ages.length) sp.set('ages', f.ages.join(','));
  if (f.colours.length) sp.set('colours', f.colours.join(','));
  if (f.sizes.length) sp.set('sizes', f.sizes.join(','));
  if (f.min !== undefined) sp.set('min', String(f.min));
  if (f.max !== undefined) sp.set('max', String(f.max));
  if (f.eco) sp.set('eco', '1');
  if (f.q) sp.set('q', f.q);
  if (f.sort !== 'popular') sp.set('sort', f.sort);
  if (f.page > 1) sp.set('page', String(f.page));
  return sp;
}

export const activeFilterCount = (f: BrowseFilters): number =>
  f.types.length + f.brands.length + f.genders.length + f.ages.length + f.colours.length + f.sizes.length +
  (f.min !== undefined || f.max !== undefined ? 1 : 0) + (f.eco ? 1 : 0);

type ListKey = 'types' | 'brands' | 'genders' | 'ages' | 'colours' | 'sizes';

export function useClothingBrowse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const [page, setPage] = useState<StylesPage | null>(null);
  const [facets, setFacets] = useState<Facets | null>(null);
  const [brandFallback, setBrandFallback] = useState<string[]>([]);
  const fallbackBrands = useRef<Promise<string[]> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Local search text so typing feels instant; the URL updates after a pause.
  const [searchText, setSearchText] = useState(filters.q);
  useEffect(() => { setSearchText(filters.q); }, [filters.q]);

  const update = useCallback((patch: Partial<BrowseFilters>, resetPage = true) => {
    const next: BrowseFilters = { ...parseFilters(searchParams), ...patch };
    if (resetPage && patch.page === undefined) next.page = 1;
    setSearchParams(serializeFilters(next), { replace: false });
  }, [searchParams, setSearchParams]);

  const toggle = useCallback((key: ListKey, value: string) => {
    const list = filters[key];
    update({ [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] });
  }, [filters, update]);

  const setList = useCallback((key: ListKey, values: string[]) => update({ [key]: values }), [update]);

  const clearAll = useCallback(() => {
    setSearchParams(serializeFilters({ ...EMPTY_FILTERS, q: filters.q, sort: filters.sort }));
  }, [filters.q, filters.sort, setSearchParams]);

  const setPageNumber = useCallback((n: number) => update({ page: n }, false), [update]);

  // Debounced search → URL
  const searchTimer = useRef<number | undefined>(undefined);
  const submitSearch = useCallback((text: string, immediate = false) => {
    setSearchText(text);
    window.clearTimeout(searchTimer.current);
    const commit = () => { if (text.trim() !== filters.q) update({ q: text.trim() }); };
    if (immediate) commit();
    else searchTimer.current = window.setTimeout(commit, 350);
  }, [filters.q, update]);

  // Data loading with cancellation
  const filterKey = serializeFilters(filters).toString();
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchStyles(filters, PAGE_SIZE, controller.signal)
      .then((res) => { setPage(res); setLoading(false); })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return;
        setError('We could not load products right now. Please try again.');
        setLoading(false);
      });

    fetchFacets(filters, controller.signal)
      .then((res) => {
        if (res) { setFacets(res); return; }
        // Facets unavailable (older API): fall back to a plain brand list once
        if (!fallbackBrands.current) {
          fallbackBrands.current = fetchBrandMeta().then((rows) => rows.map((b) => b.name).sort());
        }
        fallbackBrands.current.then((names) => setBrandFallback(names)).catch(() => undefined);
      })
      .catch(() => undefined);

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  return {
    filters,
    page,
    facets,
    brandFallback,
    loading,
    error,
    searchText,
    submitSearch,
    update,
    toggle,
    setList,
    clearAll,
    setPageNumber,
    activeCount: activeFilterCount(filters),
  };
}

export type ClothingBrowse = ReturnType<typeof useClothingBrowse>;
